import ApexCharts, { ApexOptions } from "apexcharts";
import React, { useEffect, useMemo, useRef } from "react";
import { getCSS } from "../../../assets/ts/_utils";
import { useThemeMode } from "../../layout/theme-mode/ThemeModeProvider";

// ── Dialing code → Country name + flag emoji mapping ──────────────────
const COUNTRY_CODE_MAP: Record<string, { name: string; flag: string }> = {
  "+1": { name: "United States", flag: "🇺🇸" },
  "+7": { name: "Russia", flag: "🇷🇺" },
  "+20": { name: "Egypt", flag: "🇪🇬" },
  "+27": { name: "South Africa", flag: "🇿🇦" },
  "+30": { name: "Greece", flag: "🇬🇷" },
  "+31": { name: "Netherlands", flag: "🇳🇱" },
  "+32": { name: "Belgium", flag: "🇧🇪" },
  "+33": { name: "France", flag: "🇫🇷" },
  "+34": { name: "Spain", flag: "🇪🇸" },
  "+36": { name: "Hungary", flag: "🇭🇺" },
  "+39": { name: "Italy", flag: "🇮🇹" },
  "+40": { name: "Romania", flag: "🇷🇴" },
  "+41": { name: "Switzerland", flag: "🇨🇭" },
  "+43": { name: "Austria", flag: "🇦🇹" },
  "+44": { name: "United Kingdom", flag: "🇬🇧" },
  "+45": { name: "Denmark", flag: "🇩🇰" },
  "+46": { name: "Sweden", flag: "🇸🇪" },
  "+47": { name: "Norway", flag: "🇳🇴" },
  "+48": { name: "Poland", flag: "🇵🇱" },
  "+49": { name: "Germany", flag: "🇩🇪" },
  "+51": { name: "Peru", flag: "🇵🇪" },
  "+52": { name: "Mexico", flag: "🇲🇽" },
  "+53": { name: "Cuba", flag: "🇨🇺" },
  "+54": { name: "Argentina", flag: "🇦🇷" },
  "+55": { name: "Brazil", flag: "🇧🇷" },
  "+56": { name: "Chile", flag: "🇨🇱" },
  "+57": { name: "Colombia", flag: "🇨🇴" },
  "+58": { name: "Venezuela", flag: "🇻🇪" },
  "+60": { name: "Malaysia", flag: "🇲🇾" },
  "+61": { name: "Australia", flag: "🇦🇺" },
  "+62": { name: "Indonesia", flag: "🇮🇩" },
  "+63": { name: "Philippines", flag: "🇵🇭" },
  "+64": { name: "New Zealand", flag: "🇳🇿" },
  "+65": { name: "Singapore", flag: "🇸🇬" },
  "+66": { name: "Thailand", flag: "🇹🇭" },
  "+81": { name: "Japan", flag: "🇯🇵" },
  "+82": { name: "South Korea", flag: "🇰🇷" },
  "+84": { name: "Vietnam", flag: "🇻🇳" },
  "+86": { name: "China", flag: "🇨🇳" },
  "+90": { name: "Turkey", flag: "🇹🇷" },
  "+91": { name: "India", flag: "🇮🇳" },
  "+92": { name: "Pakistan", flag: "🇵🇰" },
  "+93": { name: "Afghanistan", flag: "🇦🇫" },
  "+94": { name: "Sri Lanka", flag: "🇱🇰" },
  "+95": { name: "Myanmar", flag: "🇲🇲" },
  "+98": { name: "Iran", flag: "🇮🇷" },
  "+212": { name: "Morocco", flag: "🇲🇦" },
  "+213": { name: "Algeria", flag: "🇩🇿" },
  "+216": { name: "Tunisia", flag: "🇹🇳" },
  "+218": { name: "Libya", flag: "🇱🇾" },
  "+220": { name: "Gambia", flag: "🇬🇲" },
  "+221": { name: "Senegal", flag: "🇸🇳" },
  "+233": { name: "Ghana", flag: "🇬🇭" },
  "+234": { name: "Nigeria", flag: "🇳🇬" },
  "+254": { name: "Kenya", flag: "🇰🇪" },
  "+255": { name: "Tanzania", flag: "🇹🇿" },
  "+256": { name: "Uganda", flag: "🇺🇬" },
  "+260": { name: "Zambia", flag: "🇿🇲" },
  "+263": { name: "Zimbabwe", flag: "🇿🇼" },
  "+351": { name: "Portugal", flag: "🇵🇹" },
  "+352": { name: "Luxembourg", flag: "🇱🇺" },
  "+353": { name: "Ireland", flag: "🇮🇪" },
  "+354": { name: "Iceland", flag: "🇮🇸" },
  "+358": { name: "Finland", flag: "🇫🇮" },
  "+370": { name: "Lithuania", flag: "🇱🇹" },
  "+371": { name: "Latvia", flag: "🇱🇻" },
  "+372": { name: "Estonia", flag: "🇪🇪" },
  "+380": { name: "Ukraine", flag: "🇺🇦" },
  "+381": { name: "Serbia", flag: "🇷🇸" },
  "+385": { name: "Croatia", flag: "🇭🇷" },
  "+420": { name: "Czech Republic", flag: "🇨🇿" },
  "+421": { name: "Slovakia", flag: "🇸🇰" },
  "+880": { name: "Bangladesh", flag: "🇧🇩" },
  "+960": { name: "Maldives", flag: "🇲🇻" },
  "+961": { name: "Lebanon", flag: "🇱🇧" },
  "+962": { name: "Jordan", flag: "🇯🇴" },
  "+963": { name: "Syria", flag: "🇸🇾" },
  "+964": { name: "Iraq", flag: "🇮🇶" },
  "+965": { name: "Kuwait", flag: "🇰🇼" },
  "+966": { name: "Saudi Arabia", flag: "🇸🇦" },
  "+971": { name: "UAE", flag: "🇦🇪" },
  "+972": { name: "Israel", flag: "🇮🇱" },
  "+974": { name: "Qatar", flag: "🇶🇦" },
  "+977": { name: "Nepal", flag: "🇳🇵" },
};

/**
 * Converts a raw dialing-code label into a friendly display string.
 * For the chart legend we use a shorter format to prevent overflow.
 */
function formatCountryLabel(raw: string): string {
  const trimmed = raw.trim();

  if (!trimmed.startsWith("+")) {
    return `🌍 ${trimmed}`;
  }

  const entry = COUNTRY_CODE_MAP[trimmed];
  if (entry) {
    return `${entry.flag} ${entry.name} (${trimmed})`;
  }

  return `🏳️ Unknown (${trimmed})`;
}

type Props = {
  className: string;
  labels: string[];
  dataList: number[];
  title: string;
  eyebrow?: string;
  description?: string;
};

const CountryWiseUsersChart: React.FC<Props> = ({
  className,
  labels,
  dataList,
  title,
  eyebrow,
  description,
}) => {
  const chartRef = useRef<HTMLDivElement | null>(null);
  const { mode } = useThemeMode();
  const hasData = dataList.some((value) => value > 0);

  const friendlyLabels = useMemo(
    () => (labels || []).map(formatCountryLabel),
    [labels],
  );

  useEffect(() => {
    if (
      !chartRef.current ||
      !friendlyLabels.length ||
      !dataList.length ||
      !hasData
    ) {
      return;
    }

    const height = parseInt(getCSS(chartRef.current, "height"));
    const chart = new ApexCharts(
      chartRef.current,
      getDonutChartOptions(height, friendlyLabels, dataList),
    );
    chart.render();

    return () => {
      chart.destroy();
    };
  }, [chartRef, mode, friendlyLabels, dataList, hasData]);

  return (
    <div className={`card dashboard-chart-card ${className}`}>
      <div className="card-header border-0 pb-0">
        <div className="dashboard-chart-card__heading">
          {eyebrow ? (
            <span className="dashboard-chart-card__eyebrow">{eyebrow}</span>
          ) : null}
          <h3 className="dashboard-chart-card__title">{title}</h3>
          <p className="dashboard-chart-card__description">
            {description ?? "Distribution of users by country"}
          </p>
        </div>
      </div>
      <div className="card-body dashboard-chart-card__body dashboard-chart-card__body--donut d-flex align-items-center">
        {!hasData ? (
          <div className="dashboard-chart-empty">
            <span className="dashboard-chart-empty__icon">CW</span>
            <span className="dashboard-chart-empty__copy">
              <span className="dashboard-chart-empty__title">
                No country distribution yet
              </span>
              <span className="dashboard-chart-empty__subtitle">
                User activity will appear here as soon as data is available.
              </span>
            </span>
          </div>
        ) : null}
        <div
          ref={chartRef}
          className="dashboard-chart-canvas"
          style={{ height: "340px", width: "100%", opacity: hasData ? 1 : 0.2 }}
        ></div>
      </div>
    </div>
  );
};

export { CountryWiseUsersChart };

function getDonutChartOptions(
  height: number,
  labels: string[],
  dataList: number[],
): ApexOptions {
  const colorPalette = [
    "#0F766E",
    "#0EA5E9",
    "#F59E0B",
    "#F97316",
    "#14B8A6",
    "#2563EB",
    "#F43F5E",
    "#84CC16",
    "#06B6D4",
    "#8B5CF6",
    "#FB7185",
    "#22C55E",
    "#38BDF8",
    "#A16207",
    "#64748B",
  ];

  const total = dataList.reduce((a, b) => a + b, 0);

  return {
    series: dataList,
    chart: {
      fontFamily: "inherit",
      type: "donut",
      height: height,
      dropShadow: {
        enabled: false,
      },
      toolbar: {
        show: false,
      },
      animations: {
        enabled: true,
        easing: "easeinout",
        speed: 800,
        animateGradually: {
          enabled: true,
          delay: 120,
        },
        dynamicAnimation: {
          enabled: true,
          speed: 350,
        },
      },
    },
    labels: labels,
    colors: colorPalette.slice(0, dataList.length),
    plotOptions: {
      pie: {
        customScale: 0.96,
        expandOnClick: false,
        donut: {
          size: "74%",
          labels: {
            show: true,
            name: {
              show: true,
              fontSize: "11px",
              fontWeight: 700,
              color: "#64748B",
              offsetY: -7,
            },
            value: {
              show: true,
              fontSize: "24px",
              fontWeight: 800,
              color: "#0F172A",
              offsetY: 3,
              formatter: function (val: string) {
                return val;
              },
            },
            total: {
              show: true,
              showAlways: true,
              label: "Total",
              fontSize: "11px",
              fontWeight: 600,
              color: "#94A3B8",
              formatter: function (w: any) {
                return w.globals.seriesTotals
                  .reduce((a: number, b: number) => a + b, 0)
                  .toString();
              },
            },
          },
        },
      },
    },
    dataLabels: {
      enabled: false,
    },
    legend: {
      show: true,
      position: "bottom",
      horizontalAlign: "center",
      fontSize: "11px",
      fontWeight: 600,
      labels: {
        colors: "#334155",
      },
      markers: {
        width: 8,
        height: 8,
        offsetX: -2,
      },
      itemMargin: {
        horizontal: 10,
        vertical: 6,
      },
      formatter: function (seriesName: string, opts: any) {
        const val = opts.w.globals.series[opts.seriesIndex];
        return seriesName + ": " + val;
      },
    },
    stroke: {
      width: 4,
      colors: ["rgba(255,255,255,0.96)"],
    },
    tooltip: {
      enabled: true,
      theme: "light",
      style: {
        fontSize: "13px",
      },
      y: {
        formatter: function (val: number) {
          const pct = total > 0 ? ((val / total) * 100).toFixed(1) : "0";
          return val + " user(s) (" + pct + "%)";
        },
      },
    },
    responsive: [
      {
        breakpoint: 768,
        options: {
          chart: {
            height: 360,
          },
          legend: {
            fontSize: "10px",
          },
        },
      },
    ],
  };
}
